import { API_BASE_URL } from '../config/api';
import { fetchAvailableModels } from './modelsApi';

// Fetch all models from chat service (unfiltered)
export const fetchModels = async () => {
  const response = await fetch(`${API_BASE_URL}/chat/models`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch models');
  }
  return response.json();
};

// Fetch all models for admin settings (requires admin)
export const fetchAllModelsForSettings = async () => {
  try {
    // First get the flag that we have access to all models
    const accessResponse = await fetch(`${API_BASE_URL}/models/all`, {
      credentials: 'include'
    });
    
    if (!accessResponse.ok) {
      throw new Error('Admin access required');
    }
    
    // Then fetch all models from chat service
    return await fetchModels();
  } catch (error) {
    console.error('Error fetching all models for settings:', error);
    throw error;
  }
};

// Fetch models filtered by user permissions
export const fetchFilteredModels = async () => {
  try {
    // Get all available models
    const allModels = await fetchModels();
    
    // Get user's allowed models based on team permissions
    const userModelAccess = await fetchAvailableModels();
    
    // If user has full access, return all models
    if (userModelAccess.hasFullAccess) {
      return {
        ...allModels,
        _metadata: {
          hasFullAccess: true,
          message: userModelAccess.message
        }
      };
    }
    
    // Filter models based on allowed models
    const allowedModelIds = new Set(userModelAccess.allowedModels);
    
    // If no models are allowed, return empty structure
    if (allowedModelIds.size === 0) {
      return {
        openai: [],
        anthropic: [],
        google: [],
        xai: [],
        _metadata: {
          hasFullAccess: false,
          allowedCount: 0,
          message: userModelAccess.message
        }
      };
    }
    
    // Filter each provider's models
    const filteredModels = {};
    const knownProviders = ['openai', 'anthropic', 'google', 'xai']; // Process all provider arrays
    
    Object.keys(allModels).forEach(provider => {
      // Skip metadata and non-provider properties
      if (provider === '_metadata' || !knownProviders.includes(provider)) {
        return;
      }
      
      // Ensure the provider's value is an array
      if (Array.isArray(allModels[provider])) {
        filteredModels[provider] = allModels[provider].filter(model => 
          allowedModelIds.has(model.id)
        );
      } else {
        console.warn(`Provider ${provider} models is not an array:`, allModels[provider]);
        filteredModels[provider] = [];
      }
    });
    
    // Add metadata about filtering
    filteredModels._metadata = {
      hasFullAccess: false,
      allowedCount: userModelAccess.allowedModels.length,
      totalFiltered: Object.values(filteredModels).flat().length,
      message: userModelAccess.message
    };
    
    return filteredModels;
  } catch (error) {
    console.error('Error fetching filtered models:', error);
    // Fallback to all models if permission check fails
    return fetchModels();
  }
};

export const sendChatMessage = async ({
  message,
  userMessage,
  model,
  conversationHistory,
  enabledTools,
  enableAutoTools,
  extendedThinking,
  thinkingMode = 'instant',
  thinkingBudget = 10000,
  use1MContext = false,
  gpt5ReasoningEffort = 'minimal',
  gpt5Verbosity = 'low',
  conversationId,
  systemPrompt,
  attachedFiles = [],
  selectedMCPIds = [],
  abortSignal,
  onToken,
  onComplete,
  onError
}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      signal: abortSignal,
      body: JSON.stringify({
        message,
        userMessage, // Clean message for storage
        model,
        conversationHistory,
        enabledTools,
        enableAutoTools,
        extendedThinking,
        thinkingMode,
        thinkingBudget,
        use1MContext, // Pass 1M context flag
        gpt5ReasoningEffort,
        gpt5Verbosity,
        conversationId,
        systemPrompt,
        attachedFiles,
        selectedMCPIds // Pass selected MCP IDs
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
      
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      
      // Process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep the last incomplete line in the buffer
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'token') {
              onToken(data.content);
            } else if (data.type === 'complete') {
              onComplete(data);
              return;
            } else if (data.type === 'error') {
              onError(new Error(data.error));
              return;
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError, 'Line:', line);
          }
        }
      }
    }
    } finally {
      // Always release the reader
      reader.releaseLock();
    }
  } catch (error) {
    // Don't call onError for abort errors - they're expected
    if (error.name !== 'AbortError') {
      onError(error);
    }
  }
};